<template>
    <div>
        <v-data-table
            ref="tableRef"
            :value="richDataTableController.getSelectedRows()"
            :headers="richDataTableController.getVisibleHeaders()"
            :items="richDataTableController.getRows()"
            :options="richDataTableController.getOptions()"
            class="elevation-4 rounded-lg"
            :no-data-text="$t('No data available')"
            :no-results-text="$t('No results found')"
            :loading-text="$t('Loading')"
            style="position: relative"
            must-sort
            :mobile-breakpoint="0"
            :class="[selectable ? 'selectable-table' : '']"
            :header-props="{
                sortIcon: 'mdi-menu-up',
                sortByText: $t('Sort by')
            }"
            :hide-default-footer="!showFooter"
            :show-select="selectable"
            :dense="dense"
            fixed-header
            :item-key="richDataTableController.model.getRowKey()"
            :footer-props="getFooter()"
            :server-items-length="richDataTableController.getTotalRows()"
            @update:options="richDataTableController.setOptions($event)"
            @input="richDataTableController.onInput($event)"
        >
            <template v-if="richDataTableController.isLoading() || forceLoading" v-slot:body>
                <tr v-for="(i, index) in loadingRows" :key="index">
                    <td v-for="(header, j) in richDataTableController.getVisibleHeaders()" :key="header.value+'-'+j">
                        <v-skeleton-loader type="list-item"></v-skeleton-loader>
                    </td>
                </tr>
            </template>
            <template
                v-for="(column, columnIndex) in overridenHeaderSlots"
                v-slot:[column]="slotProps"
            >
                <slot :name="column" />
            </template>
            <template v-slot:item="{ item, select, isSelected, index }">
                <v-hover v-slot:default="{ hover }">
                    <tr
                        :class="[item.dirty ? 'dirty' : '']"
                        style="position: relative"
                        v-show="!(richDataTableController.isLoading() || forceLoading)"
                        @click="richDataTableController.onRowClick(item)"
                    >
                        <td v-if="selectable" class="px-0">
                            <v-simple-checkbox
                                class="py-2 px-4"
                                color="primary"
                                :value="isSelected"
                                @input="select"
                            />
                        </td>

                        <td v-for="(column, columnIndex) in richDataTableController.getVisibleHeaders()" :key="columnIndex">
                            <slot :name="getItemSlotName(column.value)" :item="item" :index="index">
                                <icon-button
                                    v-if="column.value === 'edit'"
                                    v-show="hover"
                                >
                                    edit
                                </icon-button>
                                <action-menu
                                    v-else-if="column.value === 'actionMenu'"
                                    :items="actionMenu.actions"
                                    @action="actionMenu.actions[$event].action(item)"
                                />
                                <div v-else>
                                    <slot name="default" :item="item" :header="column">
                                        <span :class="dense ? 'caption' : ''">
                                            {{ get(item, column.value) }}
                                        </span>
                                    </slot>
                                </div>
                            </slot>
                        </td>
                    </tr>
                </v-hover>
            </template>
        </v-data-table>
    </div>
</template>

<script>
import Vue from "vue"
import {computed, onMounted, onUnmounted, ref, watch} from "@vue/composition-api";
import errorDialog from "@/views/ErrorDialog/errorDialog"
import Topic from "@/components/RichDataTable/components/Topic";
import {$t} from "../../i18n";

export default {
    name: "RichDataTable",
    components: {
        Topic
    },
    props: {
        richDataTableController: {
            type: Object,
            required: true
        },
        selectable: {
            type: Boolean,
            default: false
        },
        dense: {
            type: Boolean,
            default: false
        },
        forceLoading: {
            type: Boolean,
            default: false
        },
        forceShowFooter: {
            type: Boolean,
            default: false
        },
        minimalisticFooter: {
            type: Boolean,
            default: false
        },
        overrideLoadingRows: {
            type: Number,
            default: null
        }
    },
    setup(props, ctx) {
        const tableRef = ref(null);

        const getItemSlotName = (name) => {
            return `item.${name}`
        }

        const getHeaderSlotName = (name) => {
            return `header.${name}`
        }

        const itemSlots = computed(() => props.richDataTableController.getAllHeaders().map(x => x.value).filter(x => ctx.slots[getItemSlotName(x)]))
        const actionMenu = computed(() => props.richDataTableController.getAllHeaders().find(x => x.value === 'actionMenu'))

        const onLoadError = (ex) => {
            errorDialog.showError(ex)
        }

        props.richDataTableController.setLoadErrorFunc(onLoadError)

        const showFooter = computed(() => {
            if (props.forceShowFooter) {
                return true
            }

            const itemsPerPageFirstOption = props.richDataTableController.getItemsPerPageOptions()[0]
            const totalRows = props.richDataTableController.getTotalRows()

            return itemsPerPageFirstOption < totalRows
        })

        const overridenHeaderSlots = computed(() => {
            return Object.keys(ctx.slots).filter(x => x.includes('header.'))
        })

        const resizeTable = async () => {
            if (!ctx.parent.$el) {
                return
            }

            if (!ctx.parent.$el.querySelector) {
                return
            }

            const dataTable = ctx.parent.$el.querySelector('.v-data-table')
            if (!dataTable) {
                return
            }

            const dataTableWrapper = dataTable.querySelector('.v-data-table__wrapper')
            if (!dataTableWrapper) {
                return
            }

            const footer = dataTable.querySelector('.v-data-footer')

            let height = window.innerHeight - dataTable.getBoundingClientRect().top - 12

            if (footer) {
                height -= footer.getBoundingClientRect().height
            }

            dataTableWrapper.style.maxHeight = height + 'px'
        }

        onMounted(() => {
            resizeTable()
            window.addEventListener('resize', resizeTable)
        })

        onUnmounted(() => {
            window.removeEventListener('resize', resizeTable)
        })

        watch(() => props.richDataTableController.isLoading(), () => {
            resizeTable()
        })

        const loadingRows = computed(() => {
            if (props.overrideLoadingRows) {
                return props.overrideLoadingRows
            }

            if (props.richDataTableController.getItemsPerPageOptions()[0]) {
                return props.richDataTableController.getItemsPerPageOptions()[0]
            }

            return 1
        })

        const getFooter = () => {
            if (props.minimalisticFooter) {
                return {
                    itemsPerPageOptions: [],
                    pageText: $t('{0}-{1} of {2}')
                }
            }
            return {
                itemsPerPageOptions: props.richDataTableController.getItemsPerPageOptions(),
                itemsPerPageAllText: $t('All'),
                itemsPerPageText: $t('Rows per page'),
                pageText: $t('{0}-{1} of {2}')
            }
        }

        return {
            getItemSlotName,
            getHeaderSlotName,
            itemSlots,
            actionMenu,
            showFooter,
            overridenHeaderSlots,
            tableRef,
            loadingRows,
            getFooter,
        }
    }
}
</script>

<style scoped lang="scss">
.v-data-table {
    ::v-deep .v-data-table__wrapper {
        border-radius: 8px;
        min-height: 275px;
    }

    &.v-data-table--dense {
        ::v-deep th {
            height: 48px !important;
            font-size: 0.75rem !important;
        }

        ::v-deep td {
            height: 33px !important;

            .caption {
                font-weight: 400;
            }
        }
    }

    @media screen and (min-width: 960px) {
        ::v-deep .v-data-footer {
            padding-right: 60px;
        }
    }

    &.selectable-table {
        ::v-deep {
            tr {
                td:nth-child(2), th:nth-child(2) {
                    padding-left: 0 !important;
                }
            }
        }
    }

    &.row-clickable {
         ::v-deep table {
            tbody {
                tr {
                    cursor: pointer;
                }
            }
        }
    }

    ::v-deep thead {
        tr {
            th {
                transition: color 0.3s ease-in-out;
                font-size: 0.875rem !important;
                font-weight: 500 !important;
                white-space: nowrap;
                color: var(--v-n3-base) !important;
                background-color: var(--v-white-base) !important;

                &.active {
                    color: var(--v-n5-base) !important;
                }

                .mdi-menu-up, .mdi-menu-down {
                    color: var(--v-g4-base) !important;
                }

                &:last-child {
                    padding-right: 8px !important;
                }
            }
        }
    }

    ::v-deep tbody {
        tr {
            &:hover {
                td {
                    background-color: var(--v-gray-lighten3);
                }
            }

            td {
                background-color: var(--v-white-base);

                a {
                    color: var(--v-main-base)
                }

                &:last-child {
                    padding-right: 8px !important;
                }
            }

            &.dirty {
                td {
                    background-color: var(--v-primary-lighten1);
                }
            }
        }
    }
}

</style>
